import { PublicLayout } from '@/components/PublicLayout'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

// ---------------------------------------------------------------------------
// Phase data — the 8 phases of every Masonyt project
// ---------------------------------------------------------------------------

interface Phase {
  num: string
  title: string
  description: string
  /** Every regular phase has an illustration; arbitration uses a large centered one */
  illustration?: { src: string; alt: string }
  /** Phase 4 (Arbitration) is a full-width emotional anchor */
  anchor?: boolean
}

const PHASES: Phase[] = [
  {
    num: '01',
    title: 'Definition',
    description:
      'You define your project goals and requirements in plain language. We help you shape a formal Proposition, a concrete document outlining exactly what needs to be built, with clear acceptance criteria.',
    illustration: {
      src: '/illustrations/working.svg',
      alt: 'Person working on project definition',
    },
  },
  {
    num: '02',
    title: 'Selection',
    description:
      'Recruit developers directly or publish your Proposition to our marketplace. Vetted developers review your requirements, assess feasibility, and submit competitive bids.',
    illustration: {
      src: '/illustrations/hiring.svg',
      alt: 'Choosing the right developer for the project',
    },
  },
  {
    num: '03',
    title: 'Alignment',
    description:
      'The chosen developer responds with a detailed Proposal. Once terms are agreed, your Proposition becomes a binding Contract, locking scope, timeline, and budget for exclusivity.',
    illustration: {
      src: '/illustrations/signing.svg',
      alt: 'Signing a project contract',
    },
  },
  {
    num: '04',
    title: 'Arbitration',
    description:
      'If the project ever stalls, Masonyt steps in. We review code, logs, and milestones. Funds held in Escrow guarantee you are refunded if delivery fails. This is your safety net, built into every project from day one.',
    illustration: {
      src: '/illustrations/safe.svg',
      alt: 'Safety net and escrow protection',
    },
    anchor: true,
  },
  {
    num: '05',
    title: 'Execution',
    description:
      'Work begins on our Staging Platform with full transparency. Track progress through your dashboard. Watch the product evolve in real time, milestone by milestone.',
    illustration: {
      src: '/illustrations/coding.svg',
      alt: 'Developer writing code on staging platform',
    },
  },
  {
    num: '06',
    title: 'Validation',
    description:
      'Before payment, Masonyt performs an independent audit. Security issues or bugs must be resolved at no cost to you. Nothing ships without a clean bill of health.',
    illustration: {
      src: '/illustrations/qa.svg',
      alt: 'Independent quality audit and validation',
    },
  },
  {
    num: '07',
    title: 'Launch',
    description:
      'Only after a clean audit does verified software move from Staging to your live production environment. Deployment is handled with zero downtime.',
    illustration: {
      src: '/illustrations/server.svg',
      alt: 'Zero-downtime deployment to production',
    },
  },
  {
    num: '08',
    title: 'Stewardship',
    description:
      'Software needs ongoing care. Designate a partner for maintenance, updates, and future feature cycles. When you are ready to grow, the process repeats as an Expansion.',
    illustration: {
      src: '/illustrations/agreement.svg',
      alt: 'Long-term partnership and support',
    },
  },
]

// ---------------------------------------------------------------------------
// Timeline step number circle — scale-in on scroll
// ---------------------------------------------------------------------------

function StepNumber({
  num,
  prefersReducedMotion,
  isAnchor,
}: {
  num: string
  prefersReducedMotion: boolean | null
  isAnchor?: boolean
}) {
  return (
    <motion.div
      className={`flex items-center justify-center rounded-full border-[3px] shadow-[0_4px_12px_rgba(45,42,38,0.06)] transition-all duration-300 hover:scale-[1.08] hover:bg-primary hover:text-primary-foreground ${
        isAnchor
          ? 'h-16 w-16 border-accent bg-accent/[0.08] text-accent hover:bg-accent hover:text-accent-foreground'
          : 'h-12 w-12 border-primary bg-card text-primary'
      }`}
      style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: isAnchor ? '22px' : '18px',
        fontWeight: 700,
      }}
      initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {num}
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Illustration container — consistent with Design Language section 8
// ---------------------------------------------------------------------------

function IllustrationContainer({
  src,
  alt,
  fromX,
  prefersReducedMotion,
  large,
}: {
  src: string
  alt: string
  fromX: number
  prefersReducedMotion: boolean | null
  large?: boolean
}) {
  return (
    <motion.div
      className={`w-full rounded-2xl border border-border bg-background p-6 transition-shadow duration-300 hover:shadow-[0_8px_32px_rgba(45,42,38,0.06)] ${
        large ? 'mx-auto max-w-[520px]' : 'max-w-[380px]'
      }`}
      initial={prefersReducedMotion ? false : { opacity: 0, x: fromX }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: EASE_STANDARD }}
    >
      <img
        src={src}
        alt={alt}
        className={`mx-auto h-auto w-full ${large ? 'max-w-[460px]' : 'max-w-[340px]'}`}
        loading="lazy"
      />
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Timeline phase card — text-only, alternating sides
// ---------------------------------------------------------------------------

function TimelineCard({
  phase,
  index,
  prefersReducedMotion,
}: {
  phase: Phase
  index: number
  prefersReducedMotion: boolean | null
}) {
  // Phase-number parity: 01/03/05/07 → text right, 02/04/06/08 → text left
  const isLeft = index % 2 === 1
  const slideX = isLeft ? -40 : 40

  return (
    <motion.div
      className={`w-full md:w-5/12 ${isLeft ? 'md:text-right' : 'md:text-left'}`}
      initial={prefersReducedMotion ? false : { opacity: 0, x: slideX }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: EASE_STANDARD }}
    >
      <h3
        className="mb-2.5 text-[22px] font-bold text-foreground"
        style={{ fontFamily: 'Fraunces, Georgia, serif' }}
      >
        {phase.title}
      </h3>
      <p className="text-[15px] leading-[1.65] text-muted-foreground">{phase.description}</p>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Page Export
// ---------------------------------------------------------------------------

export default function ProcessPage() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <PublicLayout>
      <div className="bg-background">
        {/* ================================================================
            HERO — Typography-only. No illustration. The words ARE the hero.
            ================================================================ */}
        <section className="relative overflow-hidden px-8 pb-16 pt-[140px]">
          {/* Gradient mesh background */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 70% 50% at 30% 50%, rgba(91,146,121,0.06) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 75% 35%, rgba(232,152,90,0.05) 0%, transparent 70%)',
            }}
          />

          <div className="relative z-10 mx-auto max-w-[1120px] text-center">
            <motion.div
              className="mb-5 text-xs font-bold uppercase tracking-[2.5px] text-primary"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD }}
            >
              Our Process
            </motion.div>

            <motion.h1
              className="mx-auto mb-7 max-w-[740px] text-foreground"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(42px, 5vw, 58px)',
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: '-1.5px',
              }}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0 }}
            >
              Eight Phases.
              <br />
              One{' '}
              <span className="bg-gradient-to-br from-primary to-[#6BAE8A] bg-clip-text text-transparent">
                Unbreakable
              </span>{' '}
              Process.
            </motion.h1>

            <motion.p
              className="mx-auto max-w-[540px] text-lg leading-[1.65] text-muted-foreground"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE_STANDARD, delay: 0.12 }}
            >
              Every Masonyt project follows the same disciplined lifecycle, from first conversation
              to long-term stewardship. No shortcuts, no surprises, no unfinished work.
            </motion.p>
          </div>
        </section>

        {/* ================================================================
            TIMELINE — Vertical journey with alternating left/right cards
            ================================================================ */}
        <section className="bg-card px-8 py-[120px]">
          <div className="mx-auto max-w-[1120px]">
            {/* Section header */}
            <div className="mb-24 text-center">
              <motion.div
                className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD }}
              >
                The Blueprint
              </motion.div>

              <motion.h2
                className="mb-3 text-foreground"
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: '42px',
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.65,
                  ease: EASE_STANDARD,
                  delay: 0.1,
                }}
              >
                Every Brick,
                <br />
                Laid with Purpose
              </motion.h2>

              <motion.p
                className="mx-auto max-w-[480px] text-[17px] text-muted-foreground"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.65,
                  ease: EASE_STANDARD,
                  delay: 0.2,
                }}
              >
                A vertical timeline of your project journey, from the first requirement to ongoing
                stewardship.
              </motion.p>
            </div>

            {/* Timeline spine */}
            <div className="relative">
              {/* Vertical line (desktop only) */}
              <div className="absolute left-1/2 hidden h-full w-px -translate-x-1/2 bg-border md:block" />

              {PHASES.map((phase, index) => {
                // Phase-number parity: 01/03/05/07 → text right, 02/04/06/08 → text left
                let isLeft = index % 2 === 1
                if (phase.title === 'Selection') isLeft = true
                if (phase.title === 'Execution') isLeft = false
                if (phase.title === 'Launch') isLeft = false

                // -------------------------------------------------------
                // ANCHOR PHASE (Arbitration) — full-width centered break
                // -------------------------------------------------------
                if (phase.anchor) {
                  return (
                    <div key={phase.num} className="relative mb-24">
                      {/* Center dot on the spine */}
                      <div className="relative z-10 mb-10 flex justify-center">
                        <StepNumber
                          num={phase.num}
                          prefersReducedMotion={prefersReducedMotion}
                          isAnchor
                        />
                      </div>

                      {/* Full-width centered content */}
                      <motion.div
                        className="mx-auto max-w-[680px] text-center"
                        initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{
                          duration: 0.65,
                          ease: EASE_STANDARD,
                        }}
                      >
                        <div className="mb-3 text-xs font-bold uppercase tracking-[2.5px] text-accent">
                          Your Safety Net
                        </div>
                        <h3
                          className="mb-4 text-[28px] font-bold text-foreground"
                          style={{
                            fontFamily: 'Fraunces, Georgia, serif',
                            letterSpacing: '-0.3px',
                          }}
                        >
                          {phase.title}
                        </h3>
                        <p className="mx-auto mb-10 max-w-[560px] text-base leading-[1.65] text-muted-foreground">
                          {phase.description}
                        </p>
                      </motion.div>

                      {/* Large centered illustration — the emotional anchor */}
                      {phase.illustration && (
                        <div className="flex justify-center">
                          <IllustrationContainer
                            src={phase.illustration.src}
                            alt={phase.illustration.alt}
                            fromX={0}
                            prefersReducedMotion={prefersReducedMotion}
                            large
                          />
                        </div>
                      )}
                    </div>
                  )
                }

                // -------------------------------------------------------
                // REGULAR PHASE — alternating text/illustration
                // isLeft=true  → text on left, illustration on right
                // isLeft=false → text on right, illustration on left (flex-row-reverse)
                // -------------------------------------------------------
                return (
                  <div
                    key={phase.num}
                    className={`relative mb-20 flex flex-col items-center justify-between ${
                      isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* TEXT — TimelineCard owns its own md:w-5/12 width and text alignment */}
                    <TimelineCard
                      phase={phase}
                      index={index}
                      prefersReducedMotion={prefersReducedMotion}
                    />

                    {/* CENTER — Step number on the spine */}
                    <div className="relative z-10 order-first mb-6 md:order-none md:mb-0">
                      <StepNumber num={phase.num} prefersReducedMotion={prefersReducedMotion} />
                    </div>

                    {/* ILLUSTRATION — opposite side from text */}
                    <div className="flex w-full justify-center md:w-5/12">
                      {phase.illustration && (
                        <IllustrationContainer
                          src={phase.illustration.src}
                          alt={phase.illustration.alt}
                          fromX={isLeft ? 40 : -40}
                          prefersReducedMotion={prefersReducedMotion}
                        />
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Terminal dot at the bottom of the spine */}
              <div className="relative z-10 hidden justify-center md:flex">
                <motion.div
                  className="h-3 w-3 rounded-full bg-primary"
                  initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================
            CTA — "Start Your Build"
            ================================================================ */}
        <section className="relative bg-background px-8 py-[140px] text-center">
          {/* Subtle gradient */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
            }}
          />

          <div className="relative z-10 mx-auto max-w-[1120px]">
            <motion.h2
              className="mb-4 text-foreground"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(36px, 4vw, 48px)',
                fontWeight: 700,
                letterSpacing: '-1px',
              }}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD }}
            >
              Ready to Lay
              <br />
              the First Brick?
            </motion.h2>

            <motion.p
              className="mx-auto mb-10 max-w-[460px] text-lg leading-relaxed text-muted-foreground"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.65,
                ease: EASE_STANDARD,
                delay: 0.1,
              }}
            >
              Post your project for free and let the process protect you from day one.
            </motion.p>

            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.65,
                ease: EASE_STANDARD,
                delay: 0.2,
              }}
            >
              <motion.div
                className="inline-flex"
                whileHover={
                  prefersReducedMotion
                    ? {}
                    : {
                        y: -2,
                        scale: 1.02,
                        boxShadow: '0 10px 40px rgba(232,152,90,0.35)',
                      }
                }
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Link
                  to="/post"
                  className="inline-flex items-center gap-2.5 rounded-full bg-accent px-12 py-[18px] text-lg font-bold text-accent-foreground shadow-[0_6px_28px_rgba(232,152,90,0.3)] transition-colors"
                >
                  Start Your Build
                  <ArrowRight className="h-5 w-5 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            {/* Business deal illustration — scale-in below CTA */}
            <motion.div
              className="mt-14"
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.7,
                ease: EASE_STANDARD,
                delay: 0.3,
              }}
            >
              <img
                src="/illustrations/business-deal.svg"
                alt="Business deal completed"
                className="mx-auto h-auto max-w-[400px]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>
      </div>
    </PublicLayout>
  )
}
